import Badge from "@/components/common/Badge";
import { API_BASE_URL } from "@/lib/axios";
import { useTranslation } from "react-i18next";

interface CourseHeroProps {
  course: any;
  language: string;
}

const CourseHero = ({ course, language }: CourseHeroProps) => {
  const { t } = useTranslation();

  // Use thumbnail from backend or fallback to a default image
  const imageUrl = course?.document?.thumbnail
    ? `${course.document.thumbnail}`
    : "https://images.pexels.com/photos/5380642/pexels-photo-5380642.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750";

  // Use language-aware course title and description
  const courseTitle =
    course?.document?.title?.[language] || course?.document?.title?.en || "";
  const courseDescription =
    course?.document?.courseDescription?.[language] ||
    course?.document?.courseDescription?.en ||
    "";

  return (
    <div className="relative h-56 sm:h-80 md:h-96 overflow-hidden">
      <img
        src={imageUrl}
        alt={courseTitle}
        className="h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/80"></div>
      <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-8 text-white">
        <div className="mb-2 sm:mb-4 flex items-center space-x-2 sm:space-x-3 overflow-x-auto">
          <Badge variant="primary" className="text-xs sm:text-sm px-2 sm:px-3 py-0.5 sm:py-1 whitespace-nowrap">
            {(() => {
              const category = course?.document?.category || "other";
              try {
                return t(`courses.category.${category}`);
              } catch {
                const categoryMap: Record<string, string> = {
                  phishing: "Phishing",
                  network_security: "Network Security",
                  password_hygiene: "Password Hygiene",
                  safe_browsing: "Safe Browsing",
                  other: "Other",
                };
                return categoryMap[category] || "Other";
              }
            })()}
          </Badge>
          <Badge
            variant={(() => {
              const level =
                course?.document?.courseLevel ||
                course?.document?.difficulty ||
                "beginner";
              return level === "beginner" || level === "Beginner"
                ? "success"
                : level === "intermediate" || level === "Intermediate"
                ? "warning"
                : "error";
            })()}
            className="text-xs sm:text-sm px-2 sm:px-3 py-0.5 sm:py-1 whitespace-nowrap"
          >
            {(() => {
              const level =
                course?.document?.courseLevel ||
                course?.document?.difficulty ||
                "beginner";
              try {
                return t(`courses.difficulty.${level.toLowerCase()}`);
              } catch {
                const levelMap: Record<string, string> = {
                  beginner: "Beginner",
                  intermediate: "Intermediate",
                  advanced: "Advanced",
                };
                return levelMap[level.toLowerCase()] || "Beginner";
              }
            })()}
          </Badge>
        </div>
        <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-2 sm:mb-4 leading-tight">
          {courseTitle}
        </h1>
        <p className="text-sm sm:text-xl text-gray-200 line-clamp-2 sm:line-clamp-3 max-w-4xl">
          {courseDescription}
        </p>
      </div>
    </div>
  );
};

export default CourseHero;
